import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section,
} from "../../components/Primitives";
import { ProductGrid } from "../../components/ProductCard";
import { EmailMyResult } from "../../components/EmailMyResult";
import { runRudrakshaFinder, apiError } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { AnimatePresence, EASE, EASE_OUT, motion, stepSwap } from "../../lib/motion";

const PURPOSES = [
  ["career-work", "Career & work"],
  ["business-wealth", "Business & wealth"],
  ["relationships", "Relationships & marriage"],
  ["study-learning", "Study & learning"],
  ["health-wellbeing", "Health & wellbeing"],
  ["peace-of-mind", "Peace of mind"],
  ["general", "General"],
];

const BUDGETS = [
  ["under-5000", "Under ₹5,000"],
  ["5000-25000", "₹5,000 – ₹25,000"],
  ["25000-100000", "₹25,000 – ₹1,00,000"],
  ["above-100000", "Above ₹1,00,000"],
];

function Steps({ step, total }) {
  return (
    <div className="mb-8" data-testid="finder-progress">
      <div className="flex gap-1">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className="relative h-1 flex-1 overflow-hidden bg-rule">
            <motion.span
              className="absolute inset-0 block origin-left bg-gold"
              initial={false}
              animate={{ scaleX: i <= step ? 1 : 0 }}
              transition={{ duration: 0.45, ease: EASE_OUT }}
            />
          </span>
        ))}
      </div>
      <p className="ui mt-2.5 text-xs text-dim">
        Step {Math.min(step + 1, total)} of {total} · no email required to see your result
      </p>
    </div>
  );
}

export function RudrakshaFinder() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", dob: "", time_of_birth: "", place_of_birth: "", purpose: "general", budget: "5000-25000" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async () => {
    setBusy(true);
    try {
      const r = await runRudrakshaFinder(form);
      setResult(r);
      setStep(3);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  useSEO({
    title: "Free Rudraksha Finder — Mukhi Recommendation, Priced by Mukhi",
    description:
      "A free rudraksha finder that returns a mukhi recommendation, prices by mukhi rather than size, and will tell you when you need nothing at all.",
    canonical: "/tools/rudraksha-finder",
  });

  const noResult = result?.no_bead_indicated;

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Rudraksha Finder" }]} />
      </Container>

      <Section className="pb-16">
        <Container className="max-w-2xl">
          <Eyebrow>Free, and always will be</Eyebrow>
          <H1 className="mt-4">Rudraksha Finder</H1>
          <Lede className="mt-4">
            Four steps. Your result is shown before we ask you for anything, and it can tell you that you
            need nothing.
          </Lede>

          <div className="mt-10"><Steps step={step} total={4} /></div>

          <AnimatePresence mode="wait">
            <motion.div key={step} variants={stepSwap} initial="hidden" animate="show" exit="exit">

          {step === 0 && (
            <form onSubmit={(e) => { e.preventDefault(); setStep(1); }} className="space-y-4" data-testid="finder-step-1">
              <label className="block">
                <span className="field-label">Your name</span>
                <input required value={form.name} onChange={set("name")} className="field" data-testid="finder-name" />
              </label>
              <label className="block">
                <span className="field-label">Date of birth</span>
                <input required type="date" value={form.dob} onChange={set("dob")} className="field num" data-testid="finder-dob" />
              </label>
              <label className="block">
                <span className="field-label">Exact time of birth</span>
                <input type="time" value={form.time_of_birth} onChange={set("time_of_birth")} className="field num" data-testid="finder-time" />
                <span className="ui mt-1 block text-xs text-dim">
                  Optional — it refines the traditional reading where the classical rules use it.
                </span>
              </label>
              <label className="block">
                <span className="field-label">Place of birth</span>
                <input value={form.place_of_birth} onChange={set("place_of_birth")} className="field" data-testid="finder-place" />
              </label>
              <button type="submit" className="btn btn-primary" data-testid="finder-next-1">Continue</button>
            </form>
          )}

          {step === 1 && (
            <div data-testid="finder-step-2">
              <H2 className="h2-sub">Primary purpose</H2>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {PURPOSES.map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { setForm({ ...form, purpose: v }); setStep(2); }}
                    data-testid={`finder-purpose-${v}`}
                    className={`ui border px-4 py-3 text-left text-sm transition-colors ${
                      form.purpose === v ? "border-gold text-gold" : "border-rule text-dim hover:border-gold hover:text-gold"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setStep(0)} className="link-u ui mt-6 text-sm">← Back</button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="finder-step-3">
              <H2 className="h2-sub">Budget band</H2>
              <p className="ui mt-2 text-sm text-dim">
                We ask so we can show you what we actually hold in that band, not so we can push you up one.
              </p>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {BUDGETS.map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm({ ...form, budget: v })}
                    data-testid={`finder-budget-${v}`}
                    className={`num border px-4 py-3 text-left text-sm transition-colors ${
                      form.budget === v ? "border-gold text-gold" : "border-rule text-dim hover:border-gold hover:text-gold"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn btn-secondary">Back</button>
                <button type="button" disabled={busy} onClick={run} className="btn btn-primary" data-testid="finder-run">
                  {busy ? "Calculating…" : "Show my result"}
                </button>
              </div>
            </div>
          )}
            </motion.div>
          </AnimatePresence>
        </Container>

        {step === 3 && result && (
          <Container className="mt-10">
            <motion.div
              className="card-s elev p-6 sm:p-10 lg:p-14"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: EASE }}
              data-testid="finder-result"
            >
              {noResult ? (
                <>
                  <Eyebrow>Result</Eyebrow>
                  <H2 className="mt-3">Nothing is indicated for you</H2>
                  <p className="measure ui mt-4 text-sm leading-relaxed text-dim">
                    {result.no_bead_message}
                  </p>
                </>
              ) : (
                <>
                  <Eyebrow>Result</Eyebrow>
                  <H2 className="mt-3">{result.mukhi} mukhi rudraksha</H2>
                </>
              )}

              <p className="measure ui mt-6 border-t border-rule pt-6 text-sm leading-relaxed text-dim">
                {result.honest_note}
              </p>

              {!noResult && result.matches?.length > 0 && (
                <div className="mt-10">
                  <H3 className="mb-5">What we currently hold that matches</H3>
                  <ProductGrid products={result.matches} testId="finder-matches" />
                </div>
              )}

              <div className="mt-10 border-t border-rule pt-6">
                <p className="ui text-sm text-ink">
                  {result.next_step || "Want a person to check this? Book The Proof-First Session — 20 minutes, ₹499, credited back against any purchase over ₹5,000."}
                </p>
                <Link to="/consult/suitability-session" className="btn btn-primary mt-4" data-testid="finder-book-session">
                  Get a straight answer — ₹499, credited back
                </Link>
              </div>

              <EmailMyResult
                tool="rudraksha-finder"
                summary={
                  noResult
                    ? { result: result.no_bead_message }
                    : { recommendation: `${result.mukhi} mukhi rudraksha` }
                }
              />
            </motion.div>
          </Container>
        )}
      </Section>
    </>
  );
}
